import React, { useState, useEffect } from 'react';
import Layout from '../../Global/Layout';
import axios from 'axios';
import { API_CONFIG } from '../../../contexts/constants';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Dashboard = ({ onLogout, userRole }) => {
  // State management - Hooks must be called before any conditional returns
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalAgents: 0,
    totalOrders: 0,
    totalAmount: 0
  });
  const [chartData, setChartData] = useState({
    drawBetAmounts: { labels: [], data: [] },
    monthlyNewCustomers: { labels: [], data: [] },
    latestDrawCustomers: { ordered: 0, notOrdered: 0, suspended: 0 },
    winningRatio: { winning: 0, nonWinning: 0 }
  });

  // Fetch dashboard data from API
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };

        // Fetch all data in parallel
        const [statsRes, drawBetRes, monthlyRes, latestDrawRes, winningRes] = await Promise.all([
          axios.get(`${API_CONFIG.BASE_URL}/dashboard/owner/stats`, { headers }),
          axios.get(`${API_CONFIG.BASE_URL}/dashboard/owner/draw-bet-amounts`, { headers }),
          axios.get(`${API_CONFIG.BASE_URL}/dashboard/owner/monthly-new-customers`, { headers }),
          axios.get(`${API_CONFIG.BASE_URL}/dashboard/owner/latest-draw-customers`, { headers }),
          axios.get(`${API_CONFIG.BASE_URL}/dashboard/owner/winning-ratio`, { headers })
        ]);

        if (statsRes.data.success) {
          setStats(statsRes.data.data);
        }

        if (drawBetRes.data.success && monthlyRes.data.success &&
            latestDrawRes.data.success && winningRes.data.success) {
          setChartData({
            drawBetAmounts: drawBetRes.data.data,
            monthlyNewCustomers: monthlyRes.data.data,
            latestDrawCustomers: latestDrawRes.data.data,
            winningRatio: winningRes.data.data
          });
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Check user role after hooks
	if (!['sysadmin', 'owner', 'admin'].includes(userRole)) {
		window.location.href = '/forbidden';
		return null;
	}

  // Prepare chart data from API response
  const drawBetAmountsData = {
    labels: chartData.drawBetAmounts.labels,
    datasets: [{
      label: 'ยอดเงินที่สั่งซื้อ',
      data: chartData.drawBetAmounts.data,
      borderColor: 'rgb(59, 130, 246)',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      tension: 0.1,
      fill: true
    }]
  };

  const monthlyNewCustomersData = {
    labels: chartData.monthlyNewCustomers.labels,
    datasets: [{
      label: 'ลูกค้าใหม่',
      data: chartData.monthlyNewCustomers.data,
      backgroundColor: 'rgba(34, 197, 94, 0.8)',
      borderColor: 'rgb(34, 197, 94)',
      borderWidth: 1
    }]
  };

  const latestDrawCustomersData = {
    labels: ['ลูกค้าที่สั่งซื้อ', 'ลูกค้าที่ไม่สั่งซื้อ', 'ลูกค้าที่สั่งซื้อไม่สำเร็จ'],
    datasets: [{
      label: 'จำนวน',
      data: [
        chartData.latestDrawCustomers.ordered,
        chartData.latestDrawCustomers.notOrdered,
        chartData.latestDrawCustomers.suspended
      ],
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(16, 185, 129, 0.8)',
        'rgba(239, 68, 68, 0.8)'
      ],
      borderColor: [
        'rgb(59, 130, 246)',
        'rgb(16, 185, 129)',
        'rgb(239, 68, 68)'
      ],
      borderWidth: 1
    }]
  };

  const winningRatioData = {
    labels: ['เคยถูกรางวัล', 'ไม่เคยถูกรางวัล'],
    datasets: [{
      data: [
        chartData.winningRatio.winning,
        chartData.winningRatio.nonWinning
      ],
      backgroundColor: [
        'rgba(34, 197, 94, 0.8)',
        'rgba(239, 68, 68, 0.8)'
      ],
      borderColor: [
        'rgb(34, 197, 94)',
        'rgb(239, 68, 68)'
      ],
      borderWidth: 2
    }]
  };

  // Chart options
  const lineOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'ยอดเงินที่สั่งซื้อแต่ละงวด'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return value.toLocaleString() + ' บาท';
          }
        }
      }
    }
  };

  const barOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'จำนวนลูกค้าใหม่แต่ละเดือน'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return value + ' คน';
          }
        }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'จำนวนลูกค้าที่สั่งซื้อในงวดล่าสุด'
      }
    }
  };

  const pieOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'สัดส่วนลูกค้าที่ถูกรางวัล'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const value = context.parsed;
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return context.label + ': ' + value + ' (' + percentage + '%)';
          }
        }
      }
    }
  };

  if (loading) {
    return (
      <Layout title="ข้อมูลภาพรวม" onLogout={onLogout} userRole={userRole}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">กำลังโหลดข้อมูล...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="ข้อมูลภาพรวม" onLogout={onLogout} userRole={userRole}>
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-blue-500 dark:bg-gray-800 rounded-xl shadow-card dark:shadow-carddark p-6 border border-borderc-light dark:border-borderc-dark">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4 text-white">
                <p className="text-sm font-medium">ยอดเงินที่สั่งซื้องวดนี้</p>
                <p className="text-2xl font-bold">{stats.totalAmount.toLocaleString()}</p>
                <p className="text-xs">บาท</p>
              </div>
            </div>
          </div>

          <div className="bg-red-500 dark:bg-gray-800 rounded-xl shadow-card dark:shadow-carddark p-6 border border-borderc-light dark:border-borderc-dark">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4 text-white">
                <p className="text-sm font-medium">จำนวนการสั่งซื้องวดนี้</p>
                <p className="text-2xl font-bold">{stats.totalOrders.toLocaleString()}</p>
                <p className="text-xs">รายการ</p>
              </div>
            </div>
          </div>

          <div className="bg-green-500 dark:bg-gray-800 rounded-xl shadow-card dark:shadow-carddark p-6 border border-borderc-light dark:border-borderc-dark">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4 text-white">
                <p className="text-sm font-medium">จำนวนลูกค้าทั้งหมด</p>
                <p className="text-2xl font-bold">{stats.totalCustomers.toLocaleString()}</p>
                <p className="text-xs">คน</p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-500 dark:bg-gray-800 rounded-xl shadow-card dark:shadow-carddark p-6 border border-borderc-light dark:border-borderc-dark">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4 text-white">
                <p className="text-sm font-medium">จำนวนตัวแทนทั้งหมด</p>
                <p className="text-2xl font-bold">{stats.totalAgents.toLocaleString()}</p>
                <p className="text-xs">คน</p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ยอดเครดิตที่สั่งซื้อแต่ละงวด */}
          <div className="bg-white dark:bg-surface-dark rounded-xl shadow-card dark:shadow-carddark p-6 border border-borderc-light dark:border-borderc-dark">
            <Line data={drawBetAmountsData} options={lineOptions} />
          </div>

          {/* จำนวนลูกค้าใหม่แต่ละเดือน */}
          <div className="bg-white dark:bg-surface-dark rounded-xl shadow-card dark:shadow-carddark p-6 border border-borderc-light dark:border-borderc-dark">
            <Bar data={monthlyNewCustomersData} options={barOptions} />
          </div>

          {/* จำนวนลูกค้าที่สั่งซื้อในงวดล่าสุด */}
          <div className="bg-white dark:bg-surface-dark rounded-xl shadow-card dark:shadow-carddark p-6 border border-borderc-light dark:border-borderc-dark">
            <Doughnut data={latestDrawCustomersData} options={doughnutOptions} />
          </div>

          {/* สัดส่วนลูกค้าที่สั่งซื้อถูก */}
          <div className="bg-white dark:bg-surface-dark rounded-xl shadow-card dark:shadow-carddark p-6 border border-borderc-light dark:border-borderc-dark">
            <Doughnut data={winningRatioData} options={pieOptions} />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
